import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useGameLoop } from '../Shared/hooks/useGameLoop';
import { useAudio } from '../Shared/hooks/useAudio';
import GameLayout from '../Shared/components/GameLayout/GameLayout';
import Scoreboard from '../Shared/components/Scoreboard/Scoreboard';
import { Link } from 'react-router-dom';

const UnicornRun = () => {
    const canvasRef = useRef(null);
    const { beep, ping } = useAudio();
    const assetsRef = useRef({
        unicorn: [],
        golem: [],
        bat: []
    });

    // Load Sprites
    useEffect(() => {
        const load = (prefix, count, key) => {
            for (let i = 1; i <= count; i++) {
                const img = new Image();
                img.src = `/assets/${prefix}_${i}.png`;
                img.onload = () => {
                    if (!assetsRef.current[key]) assetsRef.current[key] = [];
                    assetsRef.current[key][i - 1] = img; // store in index 0..2
                };
            }
        };
        // Load individual frames
        load("unicorn", 3, "unicorn");
        load("golem", 3, "golem");
        load("bat", 3, "bat");
    }, []);



    // Game Constants
    const UNICORN_X = 100;
    const GROUND_Y = 400; // Will be set dynamically based on height
    const SPAWN_RATE = 1.5; // Seconds
    const BASE_GAME_SPEED = 350; // Pixels per second (Initial)
    const PLATFORM_HEIGHT = 200; // Height of platforms above bottom

    // Game State
    const stateRef = useRef({
        running: false,
        score: 0,
        highScore: 0,
        enemies: [],

        particles: [],
        beams: [],
        bgOffset: 0,
        gameTime: 0,
        gameOver: false,
        // Physics
        // Physics
        uniY: 340, // 400 - 60
        vy: 0,
        jumpCount: 0,
        terrain: [], // { type: 'ground'|'platform', x, y, w }
        nextSpawnTime: 0 // Track random spawn
    });

    const [uiState, setUiState] = useState({ score: 0, gameOver: false });
    const [highScores, setHighScores] = useState([]);

    // Load High Scores
    useEffect(() => {
        const list = JSON.parse(localStorage.getItem('unicornRunScores') || "[]");
        setHighScores(list);
    }, []);

    const saveScore = useCallback((score) => {
        const list = JSON.parse(localStorage.getItem('unicornRunScores') || "[]");
        list.push({ score, date: new Date().toISOString() });
        list.sort((a, b) => b.score - a.score);
        const top = list.slice(0, 10);
        localStorage.setItem('unicornRunScores', JSON.stringify(top));
        setHighScores(top);
    }, []);

    const jump = useCallback(() => {
        const state = stateRef.current;
        if (!state.running || state.gameOver) return;

        // Double Jump
        if (state.jumpCount < 2) {
            state.vy = -18; // Increased from -14 for much longer jump
            state.jumpCount++;
            // sound?
        }
    }, []);

    const startGame = () => {
        stateRef.current = {
            running: true,
            score: 0,
            enemies: [],

            particles: [],
            beams: [],
            bgOffset: 0,
            timeSinceSpawn: 0,
            gameTime: 0,
            gameOver: false,
            highScore: stateRef.current.highScore,
            highScore: stateRef.current.highScore,
            uniY: 340,
            vy: 0,
            jumpCount: 0,
            terrain: [{ type: 'ground', x: 0, y: GROUND_Y, w: 2000 }], // Start with safe ground
            nextSpawnTime: 0
        };
        setUiState({ score: 0, gameOver: false });
    };

    const spawnEnemy = (width, height, time) => {
        const rand = Math.random();

        let type = 'rock'; // Default Level 1

        // Level 2: Score > 5
        if (stateRef.current.score >= 5) {
            if (rand < 0.3) type = 'rock';
            else if (rand < 0.65) type = 'bat';
            else type = 'goblin';
        }

        let size = 1.0;
        let yOffset = 0;

        if (type === 'rock') {
            // 30% chance of Big Rock
            if (Math.random() < 0.3) {
                size = 1.5;
                yOffset = -10; // Slightly taller
            }
        }

        let speed = 200 + (time * 5); // Speed up over time
        if (type === 'rock') speed = BASE_GAME_SPEED; // Match ground speed

        return {
            id: Math.random(),
            type,
            size, // Scale factor
            x: width + 50,
            y: (type === 'bat' ? height - 250 - Math.random() * 100
                : (type === 'rock' ? height - 50 + yOffset : height - 100)), // Adjusted Rock Y to -50
            speed,
            r: 25 * size, // Slightly smaller hitbox 
            hp: 1,
            maxHp: 1,
            passed: false // Track if passed for scoring
        };
    };

    // Terrain Spawning Logic
    const spawnTerrain = (state, width) => {
        const last = state.terrain[state.terrain.length - 1];
        if (last.x + last.w < width + 200) {
            const rand = Math.random();
            let nextType = 'ground';

            // Logic for next segment
            if (last.type === 'ground' && rand < 0.3) {
                // 30% chance for Platform
                nextType = 'platform';
            } else if (last.type === 'ground' && rand < 0.5) {
                // 20% chance for Pit
                nextType = 'pit';
            } else if (last.type === 'platform') {
                // Always go back to ground or pit after platform
                nextType = rand < 0.5 ? 'ground' : 'pit';
            }

            // Create Segment
            const nextX = last.x + last.w;

            if (nextType === 'pit') {
                // Determine pit width and next ground
                const pitWidth = 150 + Math.random() * 100; // 150-250px gap
                // Add ground AFTER the pit
                state.terrain.push({
                    type: 'ground',
                    x: nextX + pitWidth,
                    y: GROUND_Y,
                    w: 400 + Math.random() * 600
                });
            } else if (nextType === 'platform') {
                // Platform in air
                state.terrain.push({
                    type: 'platform',
                    x: nextX + 50, // slight gap before platform
                    y: GROUND_Y - PLATFORM_HEIGHT,
                    w: 300 + Math.random() * 300
                });
            } else {
                // Just Ground
                state.terrain.push({
                    type: 'ground',
                    x: nextX,
                    y: GROUND_Y,
                    w: 400 + Math.random() * 600
                });
            }
        }
    };

    const loop = useCallback((now, dt) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const width = canvas.width;
        const height = canvas.height;
        const state = stateRef.current;

        // Calculate Speed Multiplier: 1.1x every 5 points
        // Lifted out of 'if' block so it's available for both physics and enemy blocks
        const speedMultiplier = Math.pow(1.1, Math.floor(state.score / 5));
        const currentSpeed = BASE_GAME_SPEED * speedMultiplier;

        // Clear
        ctx.clearRect(0, 0, width, height);

        // Draw Background (Sky)
        const gradient = ctx.createLinearGradient(0, 0, 0, height);
        gradient.addColorStop(0, "#87CEEB");
        gradient.addColorStop(1, "#E0F7FA");
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);

        // Physics & Game Loop
        if (state.running) {
            // TERRAIN
            spawnTerrain(state, width);

            state.terrain.forEach(t => t.x -= currentSpeed * dt);
            // Cleanup old terrain
            if (state.terrain[0].x + state.terrain[0].w < -200) {
                state.terrain.shift();
            }

            // UNICORN PHYSICS
            state.vy += 0.55; // Gravity (positive goes down)
            state.uniY += state.vy;

            // Floor Collision
            // Find "floor" at Unicorn X
            const feetX = UNICORN_X;
            const feetY = state.uniY + 60; // Bottom of unicorn sprite

            let floorY = height + 500; // Default abyss
            let onGround = false;

            for (const t of state.terrain) {
                if (feetX >= t.x && feetX <= t.x + t.w) {
                    // Over this segment
                    // Check if we are landing on it
                    // Landing: We were above it, and now we are hitting it/below it
                    // Tolerance: If feetY is near t.y and vy >= 0
                    if (feetY >= t.y && feetY - state.vy <= t.y + 20) {
                        if (t.y < floorY) floorY = t.y; // Find highest floor (lowest Y value)
                    }
                }
            }

            // Hit Floor?
            if (feetY >= floorY) {
                state.uniY = floorY - 60;
                state.vy = 0;
                state.jumpCount = 0;
            }

            // Pit Death
            if (state.uniY > height) {
                state.running = false;
                state.gameOver = true;
                saveScore(state.score);
                beep(200, 1.0);
                setUiState({ score: state.score, gameOver: true });
            }
        }

        const uniY = state.uniY;
        const bounce = 0; // Animation handled by frame cycle generally, bounce removed for precise platforming

        // DRAW TERRAIN
        for (const t of state.terrain) {
            if (t.type === 'ground') {
                ctx.fillStyle = "#2ecc71";
                ctx.fillRect(t.x, t.y, t.w, height - t.y); // Ground fills down
                // Grass
                ctx.fillStyle = "#27ae60";
                ctx.beginPath();
                ctx.rect(t.x, t.y, t.w, 15);
                ctx.fill();
            } else if (t.type === 'platform') {
                ctx.fillStyle = "#8e44ad"; // Purple platforms
                ctx.fillRect(t.x, t.y, t.w, 30);
                // Detail
                ctx.fillStyle = "#9b59b6";
                ctx.fillRect(t.x, t.y, t.w, 5);
            }
        }

        // Draw Unicorn
        if (assetsRef.current.unicorn && assetsRef.current.unicorn.length >= 3) {
            // 3 Frames, cycle every ~150ms
            const frameIdx = Math.floor(now / 150) % 3;
            const img = assetsRef.current.unicorn[frameIdx];

            if (img) {
                ctx.drawImage(
                    img,
                    UNICORN_X - 60, uniY + bounce - 40, 120, 80
                );
            }
        } else {
            // Fallback
            ctx.font = "60px Arial";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText("🦄", UNICORN_X, uniY + bounce);
        }


        if (state.running) {
            state.gameTime += dt;
            state.timeSinceSpawn += dt;

            // Update Beams
            for (let i = state.beams.length - 1; i >= 0; i--) {
                const b = state.beams[i];
                b.life -= dt;
                if (b.life <= 0) state.beams.splice(i, 1);
            }

            // Spawn
            // Spawn rate decreases over time to max difficulty
            // Logic: Wait for 'nextSpawnTime'
            const currentSpawnRate = Math.max(0.5, SPAWN_RATE - (state.gameTime / 60) * 0.5);

            if (state.timeSinceSpawn > state.nextSpawnTime) {
                // Find spawn Y: Check terrain at end
                // We spawn slightly offscreen right
                const spawnX = width + 50;
                let groundYAtSpawn = height + 100;
                // Find terrain at spawnX
                const t = state.terrain.find(t => spawnX >= t.x && spawnX <= t.x + t.w);
                if (t) {
                    groundYAtSpawn = t.y;

                    // Logic: Only spawn ROCK if ground is regular Ground (t.type === 'ground')
                    // Platforms are too narrow maybe?

                    let enemiesToSpawn = [];
                    // Check if we should spawn rock
                    // Force rock if level 1, or random mix
                    // But restrict rock to GROUND only

                    // We need to move the decision logic here or make spawnEnemy smart about Type
                    // Let's make a temp 'kind' choice
                    let kind = 'rock';
                    if (stateRef.current.score >= 5) {
                        const r = Math.random();
                        if (r < 0.3) kind = 'rock';
                        else if (r < 0.65) kind = 'bat';
                        else kind = 'goblin';
                    }

                    // Override kind if on Platform -> No Rocks on platforms? User said "green floor" only.
                    if (t.type !== 'ground' && kind === 'rock') {
                        kind = 'bat'; // Fallback to flying enemy
                    }

                    const enemy = spawnEnemy(width, groundYAtSpawn, state.gameTime);
                    enemy.type = kind;

                    // Re-calc Y for new type
                    if (kind === 'bat') {
                        enemy.y = height - 250 - Math.random() * 100;
                    } else if (kind === 'rock' || kind === 'goblin') {
                        // Ground enemies
                        enemy.y = groundYAtSpawn - 50;
                        enemy.speed = BASE_GAME_SPEED; // Placeholder
                    }

                    // Fix size/hitbox if changed type? spawnEnemy defaults are generic or internally handled, 
                    // but we just blindly called it. Ideally we pass 'kind' to spawnEnemy.
                    // Let's refactor spawnEnemy call slightly to just stick with what we have but mutate properties

                    state.enemies.push(enemy);
                    state.timeSinceSpawn = 0;
                    state.nextSpawnTime = currentSpawnRate + Math.random() * 1.5;
                } else {
                    // Over pit? Maybe just spawn Bat
                    if (Math.random() < 0.5) {
                        const bat = spawnEnemy(width, height, state.gameTime);
                        bat.type = 'bat';
                        bat.y = height - 300;
                        state.enemies.push(bat);
                        state.timeSinceSpawn = 0;
                        state.nextSpawnTime = currentSpawnRate + Math.random() * 1.5;
                    }
                }
            }

            // Update Enemies
            for (let i = state.enemies.length - 1; i >= 0; i--) {
                const e = state.enemies[i];
                // Ground enemies match current speed
                const moveSpeed = (e.type === 'rock' || e.type === 'goblin') ? currentSpeed : e.speed;
                e.x -= moveSpeed * dt;

                // Check Score (Passed Unicorn)
                // Unicorn is at 100. If enemy passes 50 (safely behind), score up.
                if (!e.passed && e.x < 50) {
                    e.passed = true;
                    state.score++;
                    setUiState(prev => ({ ...prev, score: state.score }));
                    ping(); // success sound
                }

                // Collision with Unicorn
                // Unicorn is at UNICORN_X (100). Width ~60. 
                // Hitbox check: X overlap AND Y overlap

                // Enemy/Obstacle rect with Size
                const eh = 80 * (e.size || 1);
                const ew = 80 * (e.size || 1);

                // Centered anchor correction
                // Our draws are center-ish or top-left offset. 
                // e.x is center X. e.y is roughly bottom/center Y.
                // Draw logic uses -40 offset.

                const ex = e.x - (ew / 2);
                const ey = e.y - (eh / 2);

                // Unicorn rect
                const ux = UNICORN_X - 40;
                const uy = uniY + bounce - 40; // This includes jumpY!
                const uw = 80;
                const uh = 80;

                // AABB Collision
                if (
                    ux < ex + ew &&
                    ux + uw > ex &&
                    uy < ey + eh &&
                    uy + uh > ey
                ) {
                    state.running = false;
                    state.gameOver = true;
                    saveScore(state.score);
                    ping();
                    beep(200, 1.0);
                    setUiState({ score: state.score, gameOver: true });
                }
            }

            // Update Particles
            for (let i = state.particles.length - 1; i >= 0; i--) {
                const p = state.particles[i];
                p.life -= dt;
                p.x += p.vx * dt;
                p.y += p.vy * dt;
                if (p.life <= 0) state.particles.splice(i, 1);
            }
        }
        // Draw Beams
        for (const b of state.beams) {
            const opacity = b.life / b.maxLife;
            // Rainbow gradient
            const grad = ctx.createLinearGradient(b.sx, b.sy, b.tx, b.ty);
            grad.addColorStop(0, `hsla(0, 100%, 50%, ${opacity})`);
            grad.addColorStop(0.2, `hsla(60, 100%, 50%, ${opacity})`);
            grad.addColorStop(0.4, `hsla(120, 100%, 50%, ${opacity})`);
            grad.addColorStop(0.6, `hsla(180, 100%, 50%, ${opacity})`);
            grad.addColorStop(0.8, `hsla(240, 100%, 50%, ${opacity})`);
            grad.addColorStop(1, `hsla(300, 100%, 50%, ${opacity})`);

            ctx.lineWidth = 8;
            ctx.lineCap = 'round';
            ctx.strokeStyle = grad;
            ctx.beginPath();
            ctx.moveTo(b.sx, b.sy);
            ctx.lineTo(b.tx, b.ty);
            ctx.stroke();
        }

        // Draw Enemies
        for (const e of state.enemies) {
            // Bobbing for bat
            let dy = 0;
            if (e.type === 'bat') dy = Math.sin(now / 100) * 10;

            const enemyFrames = e.type === 'goblin' ? assetsRef.current.golem : assetsRef.current.bat;

            if (enemyFrames && enemyFrames.length >= 3) {
                // Animation frame
                const frameIdx = Math.floor(now / 150) % 3;
                const img = enemyFrames[frameIdx];

                if (img) {
                    ctx.save();
                    ctx.translate(e.x, e.y + dy);

                    ctx.drawImage(
                        img,
                        -40, -40, 80, 80
                    );
                    ctx.restore();
                }
            } else if (e.type === 'rock') {
                // Draw Rock
                const size = e.size || 1;
                ctx.font = `${60 * size}px Arial`;
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.fillText("🪨", e.x, e.y);
            } else {
                ctx.font = "50px Arial";
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                const emoji = e.type === 'bat' ? "🦇" : "👺";
                ctx.fillText(emoji, e.x, e.y + dy);
            }
        }

        // Draw Particles
        for (const p of state.particles) {
            ctx.fillStyle = p.color || `rgba(255, 215, 0, ${p.life})`;
            ctx.globalAlpha = p.life;
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
            ctx.fill();
            ctx.globalAlpha = 1.0;
        }

        // Game Over Overlay
        if (state.gameOver) {
            ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
            ctx.fillRect(0, 0, width, height);

            ctx.fillStyle = "#fff";
            ctx.font = "bold 60px Inter";
            ctx.fillText("GAME OVER", width / 2, height / 2 - 50);

            ctx.font = "30px Inter";
            ctx.fillText(`Score: ${state.score}`, width / 2, height / 2 + 20);
            ctx.fillText("Click to Restart", width / 2, height / 2 + 80);
        }

        // Score HUD
        ctx.fillStyle = "black";
        ctx.font = "bold 24px Inter";
        ctx.textAlign = "left";
        ctx.fillText(`Score: ${state.score}`, 20, 40);

        // Flashing Text
        if (!state.gameOver) {
            const wrapOpacity = (Math.sin(now / 300) + 1) / 2; // 0 to 1 cycle
            ctx.fillStyle = `rgba(50, 50, 50, ${0.5 + wrapOpacity * 0.5})`;
            ctx.font = "italic 16px Inter";
            ctx.textAlign = "center";
            ctx.fillText("Escort the baby unicorn back home to its mommy!", width / 2, height - 20);
        }

    }, [saveScore, beep, ping]);

    useGameLoop(loop, true);

    const handleCanvasClick = (e) => {
        const state = stateRef.current;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;

        if (state.gameOver) {
            startGame();
            return;
        }

        if (!state.running) {
            startGame(); // Start on first click
            return;
        }

        const groundY = canvas.height - 50;
        // FIX: Revert bounce var
        const bounce = 0;
        // Horn pos approx: x=135, y=uniY+bounce-20
        const hornX = 135;
        const hornY = stateRef.current.uniY + bounce - 25; // Use absolute Y

        // Add Laser Beam
        state.beams.push({
            sx: hornX, sy: hornY,
            tx: mx, ty: my,
            life: 0.15, maxLife: 0.15
        });

        // Check Hits
        // Simple circle hitbox check
        let hit = false;
        for (let i = state.enemies.length - 1; i >= 0; i--) {
            const e = state.enemies[i];

            // Adjust hit pos for bobbing if bat
            let ey = e.y;
            if (e.type === 'bat') ey += Math.sin(performance.now() / 100) * 10;

            const dist = Math.hypot(mx - e.x, my - ey);
            const hitThreshold = e.type === 'rock' ? e.r + 40 : e.r + 20; // Easier to hit rocks

            if (dist < hitThreshold) { // generous hitbox
                // HIT!
                state.enemies.splice(i, 1);
                state.score++;
                setUiState(prev => ({ ...prev, score: state.score }));
                hit = true;

                // Spawn Particles (Gray for rocks, Rainbow for enemies)
                const isRock = e.type === 'rock';
                const rainbow = ['#e74c3c', '#e67e22', '#f1c40f', '#2ecc71', '#3498db', '#9b59b6'];
                const grays = ['#7f8c8d', '#95a5a6', '#bdc3c7'];

                for (let k = 0; k < 15; k++) {
                    state.particles.push({
                        x: e.x,
                        y: ey,
                        vx: (Math.random() - 0.5) * 300,
                        vy: (Math.random() - 0.5) * 300,
                        life: 0.5 + Math.random() * 0.5,
                        size: 3 + Math.random() * 6,
                        color: isRock
                            ? grays[Math.floor(Math.random() * grays.length)]
                            : rainbow[Math.floor(Math.random() * rainbow.length)]
                    });
                }

                // Sound
                beep(600 + Math.random() * 400, 0.1);
                break; // Only hit one per click
            }
        }

        if (!hit && state.running) {
            // Miss sound? 
            // beep(100, 0.05);
        }
    };

    const sidebarContent = (
        <>
            <div style={{ marginBottom: '20px' }}>
                <h3 style={{ marginTop: 0 }}>Unicorn Run</h3>
                <p style={{ fontSize: '13px', color: '#aaa', marginBottom: '10px' }}>
                    Defend the running unicorn!
                </p>
                <div style={{ background: '#333', padding: '10px', borderRadius: '8px', fontSize: '13px', lineHeight: '1.4' }}>
                    <p style={{ margin: '0 0 5px 0' }}>🪨 <strong>Level 1</strong>: Avoid Rocks! (Jump or Shoot)</p>
                    <p style={{ margin: '0 0 5px 0' }}>👺 <strong>Level 2</strong>: Survive Enemies!</p>
                    <p style={{ margin: '0' }}>Get to Score 5 for Level 2</p>
                </div>
            </div>

            <button onClick={startGame} style={{
                background: GAME_THEME.BUTTON_BG,
                padding: '12px', width: '100%', border: '1px solid #444', borderRadius: '6px',
                color: 'white', fontWeight: 'bold', cursor: 'pointer', fontSize: '16px',
                transition: 'all 0.2s'
            }}>
                {uiState.gameOver ? 'Restart Game' : (stateRef.current.running ? 'Running...' : 'Start Game')}
            </button>
            <br /><br />

            <Scoreboard
                stats={{
                    "Score": uiState.score
                }}
                highScores={highScores}
                title="Top Runs"
                onReset={() => {
                    localStorage.removeItem('unicornRunScores');
                    setHighScores([]);
                }}
            />
        </>
    );

    return (
        <GameLayout
            gameTitle="Unicorn Run"
            sidebarContent={sidebarContent}
            canvasRef={canvasRef}
            onCanvasClick={handleCanvasClick}
            gameRunning={true}
        >
            {/* DOM Layer for Buttons/UI */}
            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'hidden' }}>
                <button
                    onMouseDown={jump}
                    onTouchStart={jump}
                    style={{
                        position: 'absolute',
                        left: '50%', // Center horizontally
                        bottom: '20px', // Below ground (Game canvas height is fixed?)
                        // If canvas is full height, we need to be careful.
                        // Assuming standard desktop view, 20px from bottom is good.
                        transform: 'translateX(-50%)',
                        pointerEvents: 'auto',

                        // Arcade Style
                        width: '120px',
                        height: '120px',
                        borderRadius: '50%',
                        border: '4px solid #444',
                        background: GAME_THEME.BUTTON_BG,
                        boxShadow: '0 0 20px rgba(0, 0, 0, 0.6), inset 0 0 10px rgba(255,255,255,0.1)',
                        color: 'white',
                        fontWeight: '900',
                        fontSize: '20px',
                        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.1s',
                        zIndex: 10,
                        userSelect: 'none'
                    }}
                    onMouseUp={e => e.currentTarget.blur()}
                >
                    <span>JUMP</span>
                    <span style={{ fontSize: '32px' }}>↟</span>
                </button>
            </div>

        </GameLayout >
    );
};

export default UnicornRun;
